/**
 * /reset-password/confirm?token=... · Public-Form mit Token aus URL-Query.
 * POST /api/auth/reset-password { token, new_password }.
 */
import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

export function PasswordResetConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sprint 55 (Punkt 15) · Local-Success-State + history.replaceState verhindern
  // dass Browser-Back zur Form mit usedem Token zurueckfuehrt.
  const [success, setSuccess] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (pw.length < 12) {
      setError('Passwort muss mindestens 12 Zeichen lang sein.');
      return;
    }
    if (pw !== pw2) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await api('/api/auth/reset-password', {
        method: 'POST',
        body: { token, new_password: pw },
        skipAuth: true,
      });
      // Sprint 55 (Punkt 15) · Token-Param aus URL entfernen + Form
      // ausblenden. Browser-Back kommt dann auf success-Page nicht auf Form.
      window.history.replaceState({}, '', '/reset-password/confirm');
      setSuccess(true);
      setPw('');
      setPw2('');
    } catch (err) {
      const e = err as { detail?: string; status?: number };
      if (e.status === 400) {
        setError(
          'Token ungültig oder bereits verwendet. '
          + 'Wenn du gerade ein neues Passwort gesetzt hast, ist alles in Ordnung — '
          + 'du kannst dich jetzt einloggen.',
        );
      } else {
        setError(e.detail ?? 'Reset fehlgeschlagen');
      }
    } finally {
      setBusy(false);
    }
  }

  // Sprint 55 (Punkt 15) · Success-Screen statt navigate · Browser-Back fuehrt
  // hierher zurueck (nicht auf die Form mit usedem Token).
  if (success) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title auth-title--small">PASSWORT GESETZT</h1>
          <p className="auth-claim" style={{ marginBottom: 24 }}>
            Dein neues Passwort ist aktiv. Du kannst dich jetzt einloggen.
          </p>
          <button
            type="button"
            className="auth-submit"
            onClick={() => navigate('/login', { replace: true })}
          >
            ZUM LOGIN
          </button>
        </div>
      </main>
    );
  }

  if (!token) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title auth-title--small">UNGÜLTIGER LINK</h1>
          <p>Kein Reset-Token in der URL. Bitte neuen Link anfordern.</p>
          <Link to="/reset-password" className="auth-submit" style={{ display: 'inline-block' }}>
            NEUEN LINK ANFORDERN
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title auth-title--small">NEUES PASSWORT</h1>
        <p className="auth-claim">Mindestens 12 Zeichen</p>
        <form onSubmit={submit} className="auth-form">
          <div className="auth-field">
            <label className="auth-field__label" htmlFor="rc-pw">NEUES PASSWORT</label>
            <input
              id="rc-pw"
              type="password"
              className="auth-input"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              minLength={12}
              required
              autoFocus
              autoComplete="new-password"
            />
          </div>
          <div className="auth-field">
            <label className="auth-field__label" htmlFor="rc-pw2">BESTÄTIGEN</label>
            <input
              id="rc-pw2"
              type="password"
              className="auth-input"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              minLength={12}
              required
              autoComplete="new-password"
            />
          </div>
          {error ? <p className="auth-error">{error}</p> : null}
          <button type="submit" className="auth-submit" disabled={busy}>
            {busy ? 'SETZT…' : 'PASSWORT SETZEN'}
          </button>
        </form>
      </div>
    </main>
  );
}
